import { useState } from "react";
import {
  PROVIDER_PRESETS,
  findProviderPreset,
  testConnection,
} from "@cc/agents/browser";
import type { ConnectionTestResult } from "@cc/agents/browser";
import type { AiConfig } from "./crickets";
import type { Dict } from "./i18n";
import { ICON_CHOICES, isIconImage, prefetchIcon } from "./icons";

export interface AiDraft {
  readonly providerId: string;
  readonly baseUrl: string;
  readonly model: string;
  readonly apiKey: string;
  readonly name: string;
  /**
   * Ball icon. Empty string means "use the provider's default logo".
   * Otherwise an emoji from the picker (or a pasted glyph).
   */
  readonly icon: string;
}

export function emptyDraft(): AiDraft {
  const preset = PROVIDER_PRESETS[0]!;
  prefetchIcon(preset.icon);
  return {
    providerId: preset.id,
    // Store the URL the user should see (the real endpoint); the resolved,
    // proxied form is applied at call time by `toAiConfig`.
    baseUrl: preset.baseUrl,
    model: preset.models[0] ?? "",
    apiKey: "",
    name: "",
    // Empty = provider default logo (e.g. DeepSeek whale mark).
    icon: "",
  };
}

/**
 * The optional AI panel.
 *
 * Three deliberate decisions, each of which is a claim about honesty rather than
 * a styling choice:
 *
 *  1. **The key is never stored, and the UI says so.** No login, no saved roster,
 *     nothing persisted. The note under the field is not legal boilerplate; it is
 *     the actual behaviour, and it is the reason this panel can exist in a
 *     purely client-side app without a backend to protect anything.
 *  2. **Test Connection is offered before committing.** Without it, the first
 *     sign of a bad key is a cricket that falls back to a bot and loses, which
 *     reads as "the AI is bad" rather than "your key is wrong". The test also
 *     distinguishes a bad key from a bad model name from a dead network, because
 *     those need three different fixes.
 *  3. **Nothing starts until the user presses the button.** Editing a key must
 *     not silently restart a match in progress.
 */
export function AiPanel({
  draft,
  onChange,
  active,
  onEnable,
  onDisable,
  d,
  title,
  /** When true, only the form + connection test are shown (duel modal). */
  formOnly = false,
  idPrefix = "ai",
}: {
  draft: AiDraft;
  onChange: (next: AiDraft) => void;
  active: boolean;
  onEnable?: () => void;
  onDisable?: () => void;
  d: Dict;
  title?: string;
  formOnly?: boolean;
  /** Prefix for form control ids when two panels share a page. */
  idPrefix?: string;
}) {
  const [status, setStatus] = useState<"idle" | "testing" | ConnectionTestResult["ok"] | "fail">(
    "idle",
  );
  const [result, setResult] = useState<ConnectionTestResult | null>(null);
  const preset = findProviderPreset(draft.providerId);
  const effectiveIcon = draft.icon.trim() || preset.icon;
  prefetchIcon(preset.icon);

  const pickProvider = (id: string) => {
    const next = findProviderPreset(id);
    prefetchIcon(next.icon);
    onChange({
      ...draft,
      providerId: id,
      // Fill the known defaults so the common case is one paste and no typing.
      baseUrl: next.baseUrl,
      model: next.models[0] ?? "",
      // Switching provider resets to that provider's logo unless the user had
      // already picked a custom emoji (non-URL icon).
      icon: draft.icon && !isIconImage(draft.icon) ? draft.icon : "",
    });
  };

  const runTest = async () => {
    setStatus("testing");
    setResult(null);
    const outcome = await testConnection({
      id: "test",
      persona: "test",
      providerId: draft.providerId,
      baseUrl: draft.baseUrl,
      model: draft.model,
      apiKey: draft.apiKey,
    });
    setResult(outcome);
    setStatus(outcome.ok ? true : "fail");
  };

  const canEnable = draft.apiKey.trim() !== "" && draft.model.trim() !== "";
  const shownUrl = draft.baseUrl || preset.baseUrl;

  return (
    <div className="panel ai-panel">
      <div className="panel-head">
        <span>{title ?? d.aiPanelTitle}</span>
        {!formOnly && active ? (
          <span className="ai-active-badge">{d.aiActive}</span>
        ) : null}
      </div>
      <div className="panel-body">
        {!formOnly ? <p className="hint">{d.aiPanelIntro}</p> : null}

        <label className="field">
          <span>{d.aiProvider}</span>
          <select value={draft.providerId} onChange={(e) => pickProvider(e.target.value)}>
            {PROVIDER_PRESETS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>{d.aiModel}</span>
          {preset.models.length > 0 ? (
            <input
              list={`${idPrefix}-model-options`}
              value={draft.model}
              onChange={(e) => onChange({ ...draft, model: e.target.value })}
              placeholder={preset.models[0]}
            />
          ) : (
            <input
              value={draft.model}
              onChange={(e) => onChange({ ...draft, model: e.target.value })}
              placeholder="model-name"
            />
          )}
          <datalist id={`${idPrefix}-model-options`}>
            {preset.models.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
        </label>

        <label className="field">
          <span>{d.aiApiKey}</span>
          <input
            type="password"
            value={draft.apiKey}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => onChange({ ...draft, apiKey: e.target.value })}
            placeholder="sk-…"
          />
        </label>

        {/* Only shown for custom endpoints, where the path is actually the
            user's to choose. A preset's URL is fixed and shown as text below. */}
        {preset.custom ? (
          <label className="field">
            <span>{d.aiBaseUrl}</span>
            <input
              value={draft.baseUrl}
              onChange={(e) => onChange({ ...draft, baseUrl: e.target.value })}
              placeholder="https://…/v1"
            />
          </label>
        ) : (
          <div className="field">
            <span>{d.aiBaseUrl}</span>
            <code className="ai-endpoint">{shownUrl}</code>
          </div>
        )}

        <label className="field">
          <span>{d.aiName}</span>
          <input
            value={draft.name}
            maxLength={18}
            onChange={(e) => onChange({ ...draft, name: e.target.value })}
            placeholder={d.aiNamePlaceholder}
          />
        </label>

        <div className="field">
          <span>{d.aiIcon}</span>
          <div className="ai-icon-row">
            <button
              type="button"
              className={`ai-icon-chip ${!draft.icon.trim() ? "active" : ""}`}
              onClick={() => onChange({ ...draft, icon: "" })}
              title={d.aiIconDefault}
              aria-label={d.aiIconDefault}
            >
              <IconPreview icon={preset.icon} />
            </button>
            {ICON_CHOICES.map((glyph) => (
              <button
                type="button"
                key={glyph}
                className={`ai-icon-chip ${draft.icon === glyph ? "active" : ""}`}
                onClick={() => onChange({ ...draft, icon: glyph })}
                aria-label={glyph}
              >
                {glyph}
              </button>
            ))}
          </div>
          <p className="hint ai-icon-hint">
            {d.aiIconHint} · {d.aiIconPreview}:{" "}
            <IconPreview icon={effectiveIcon} />
          </p>
        </div>

        <div className="ai-actions">
          <button onClick={runTest} disabled={!canEnable || status === "testing"}>
            {status === "testing" ? d.aiTesting : d.aiTest}
          </button>
          {!formOnly && onEnable && onDisable ? (
            active ? (
              <button className="ghost" onClick={onDisable}>
                {d.aiDisable}
              </button>
            ) : (
              <button className="primary" onClick={onEnable} disabled={!canEnable}>
                {d.aiEnable}
              </button>
            )
          ) : null}
        </div>

        {result ? (
          <p className={`ai-status ${result.ok ? "ok" : "bad"}`}>
            {result.ok
              ? d.aiConnOk(result.latencyMs, result.reply)
              : result.stage === "auth"
                ? d.aiConnAuth
                : result.stage === "model"
                  ? d.aiConnModel
                  : d.aiConnNetwork}
          </p>
        ) : null}

        <p className="hint ai-security">{d.aiSecurity}</p>
      </div>
    </div>
  );
}

function IconPreview({ icon }: { icon: string }) {
  if (isIconImage(icon)) {
    return <img className="ai-icon-img" src={icon} alt="" width={22} height={22} />;
  }
  return <span className="ai-icon-glyph">{icon}</span>;
}

/** Builds the config to hand to the arena. */
export function toAiConfig(draft: AiDraft, fallbackName = "AI CRICKET"): AiConfig {
  const preset = findProviderPreset(draft.providerId);
  const icon = draft.icon.trim() || preset.icon;
  return {
    providerId: draft.providerId,
    baseUrl: draft.baseUrl.trim() || preset.baseUrl,
    model: draft.model.trim(),
    apiKey: draft.apiKey,
    name: draft.name.trim() || fallbackName,
    icon,
  };
}

/** Rehydrates a panel draft from a session AI config. */
export function draftFromConfig(config: AiConfig): AiDraft {
  const preset = findProviderPreset(config.providerId);
  const stored = config.icon?.trim() ?? "";
  // Provider-default logos are stored as the resolved URL; show that as
  // "default selected" in the form (empty icon field) so switching providers
  // still updates the mark.
  const isProviderDefault = !stored || stored === preset.icon;
  return {
    providerId: config.providerId,
    baseUrl: config.baseUrl,
    model: config.model,
    apiKey: config.apiKey,
    name: config.name,
    icon: isProviderDefault ? "" : stored,
  };
}
